import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button, Divider } from "@mui/material";
import { toast } from "react-toastify";
import EditOutlined from "@mui/icons-material/EditOutlined";

import { FeedbackDetailContext } from "contexts/FeedbackDetailContext";

import FeedbackBasicDetail from "./feedbackComponents/FeedbackBasicDetail";
import FeedbackResponses from "./feedbackComponents/FeedbackResponsesTab";
import StagePathway from "components/Shared/StagePathway";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import CustomTabs from "components/Shared/CustomTabs";
import CustomTabPanel from "components/Shared/CustomTabPanel";
import FeedbackHistoryTab from "./feedbackComponents/FeedbackHistoryTab";

import {
  useGetFeedbackDetailQuery,
  useUpdateFeedbackMutation,
} from "store/services/feedbackApi";

import { useAppMeta } from "hooks";

const FeedbackDetailScreen = () => {
  const { id } = useParams();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "responses";

  const breadcrumbs = [
    {
      title: "Feedbacks",
      path: searchParams.get("prevTab")
        ? `/app/crm/feedback/list?tab=${searchParams.get("prevTab")}`
        : "/app/crm/feedback/list",
    },
    { title: "Feedback Detail" },
  ];

  const feedbackStages = [
    "Open",
    "In Progress",
    "Verification",
    "Closed",
    "Discarded",
  ];

  const feedbackDetailTabs = [
    {
      label: "Responses",
      value: "responses",
    },
    {
      label: "History",
      value: "history",
    },
  ];

  // RTK Query
  const {
    data: feedbackDetail,
    isLoading: loadingFeedback,
    error,
  } = useGetFeedbackDetailQuery(id);

  const [updateFeedback, { isLoading: updatingFeedback }] =
    useUpdateFeedbackMutation();

  // methods
  const handleChangeFeedbackStage = async (stage) => {
    try {
      await updateFeedback({
        _id: feedbackDetail?._id,
        stage,
        feedbackComment: `Changed stage from "${feedbackDetail?.stage}" to "${stage}"`,
      }).unwrap();
      toast.info("Feedback's stage updated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update feedback's stage!");
    }
  };

  useAppMeta(
    !feedbackDetail ? "Feedback Detail" : `Feedback - ${feedbackDetail?.title}`
  );

  const PageActions = () => (
    <Link to={`/app/crm/feedback/${id}/edit`}>
      <Button variant="contained" startIcon={<EditOutlined />}>
        Edit
      </Button>
    </Link>
  );

  return (
    <FeedbackDetailContext.Provider value={{ feedbackDetail, loadingFeedback }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingFeedback || updatingFeedback}
        error={error}
        headerActions={<PageActions />}
        disablePaper
      >
        <BreadcrumbLayout.Error error={error} />

        {!!feedbackDetail?.stage && (
          <StagePathway
            stages={feedbackStages}
            activeStage={feedbackDetail?.stage}
            getStageLabel={(stage) => stage}
            getStageValue={(stage) => stage}
            onChange={handleChangeFeedbackStage}
          />
        )}

        <BreadcrumbLayout.Paper>
          <FeedbackBasicDetail />

          <Divider />

          <CustomTabs tabs={feedbackDetailTabs} activeTab={activeTab} />

          {!loadingFeedback ? (
            <>
              <CustomTabPanel activeTab={activeTab} value="responses">
                <FeedbackResponses />
              </CustomTabPanel>

              <CustomTabPanel activeTab={activeTab} value="history">
                <FeedbackHistoryTab />
              </CustomTabPanel>
            </>
          ) : (
            <RowsSkeleton />
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </FeedbackDetailContext.Provider>
  );
};

export default FeedbackDetailScreen;
